import React, { useEffect, useState } from 'react';
import { Layout, Menu, Avatar, Tag, Drawer } from 'antd';
import {
  ShoppingCartOutlined,
  DollarOutlined,
  FileDoneOutlined,
  DatabaseOutlined,
  ProfileOutlined,
  ThunderboltOutlined,
  RightOutlined,
  CloseOutlined,
} from '@ant-design/icons';
import { useLocation, useNavigate } from 'react-router-dom';
import './sidebar.css';
import { useAuth } from '../../context/auth-context';

const { Sider } = Layout;

const MENU_KEYS = [
  '/',
  '/catalogo',
  '/inventario',
  '/precios',
  '/logs',
] as const;

/**
 * Resolves which sidebar item matches the current route.
 * Detail routes like /logs/:id keep Logs selected.
 */
const resolveSelectedMenuKey = (pathname: string): string => {
  if (pathname === '/') return '/';

  const match = MENU_KEYS.filter((key) => key !== '/').find(
    (key) => pathname === key || pathname.startsWith(`${key}/`)
  );

  return match ?? '/';
};

interface UserData {
  sub: string;
  roles: string[];
  exp: number;
}

interface SidebarProps {
  mobileNavOpen?: boolean;
  onCloseMobileNav?: () => void;
}

const useUserData = (): UserData | null => {
  const [userData, setUserData] = useState<UserData | null>(null);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        setUserData(payload);
      } catch (error) {
        console.error('Error decoding token', error);
      }
    }
  }, []);

  return userData;
};

const Sidebar: React.FC<SidebarProps> = ({
  mobileNavOpen = false,
  onCloseMobileNav,
}) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const selectedMenuKey = resolveSelectedMenuKey(pathname);
  const userData = useUserData();
  const { logout } = useAuth();

  const isTokenExpired = (exp: number): boolean => {
    const currentTime = Math.floor(Date.now() / 1000);
    return exp < currentTime;
  };

  useEffect(() => {
    if (userData) {
      const checkTokenExpiration = setInterval(() => {
        if (isTokenExpired(userData.exp)) {
          localStorage.removeItem('token');
          logout();
        }
      }, 1000);

      return () => clearInterval(checkTokenExpiration);
    }
  }, [userData, logout]);

  const getInitials = (name: string) => {
    const names = name.split(' ');
    return names.map((n) => n[0]).join('');
  };

  /**
   * Strips ROLE_ prefix for comparison/display.
   * ASSUMPTION: JWT roles follow Spring-style ROLE_* naming.
   */
  const normalizeRoleKey = (role: string) =>
    role.replace(/^ROLE_/i, '').replace(/_/g, ' ').trim().toLowerCase();

  const formatRoleLabel = (role: string) => {
    const withoutPrefix = role.replace(/^ROLE_/i, '').replace(/_/g, ' ').trim();
    if (!withoutPrefix) return role;
    return withoutPrefix.charAt(0).toUpperCase() + withoutPrefix.slice(1).toLowerCase();
  };

  const displayName = userData?.sub ?? 'Usuario';
  // Hide roles that only repeat the username (e.g. admin + ROLE_ADMIN).
  const distinctRoles =
    userData?.roles.filter(
      (role) => normalizeRoleKey(role) !== displayName.trim().toLowerCase()
    ) ?? [];

  const handleMenuClick = ({ key }: { key: string }) => {
    navigate(key);
    onCloseMobileNav?.();
  };

  const menuItems = [
    {
      key: '/',
      icon: <ShoppingCartOutlined />,
      label: 'Órdenes',
    },
    {
      key: '/catalogo',
      icon: <FileDoneOutlined />,
      label: 'Catálogo',
    },
    {
      key: '/inventario',
      icon: <DatabaseOutlined />,
      label: 'Inventario',
    },
    {
      key: '/precios',
      icon: <DollarOutlined />,
      label: 'Precios',
    },
    {
      key: '/logs',
      icon: <ProfileOutlined />,
      label: 'Logs',
    },
  ];

  const renderSidebarPanel = (options?: { showClose?: boolean }) => (
    <section className="sidebar-card">
      <div className="sidebar-top">
        <div className="sidebar-user">
          <Avatar
            size={36}
            className="sidebar-avatar"
            style={{
              backgroundColor: '#E24585',
              color: '#fff',
              fontSize: '15px',
            }}
          >
            {userData ? getInitials(userData.sub) : 'JD'}
          </Avatar>
          <div className="sidebar-user-info">
            <span className="sidebar-user-greeting">Bienvenido</span>
            <span className="p-user-info" title={displayName}>
              {displayName}
            </span>
            {distinctRoles.map((role) => (
              <Tag
                key={role}
                className="sidebar-user-role"
                color="#E24585"
                title={role}
              >
                {formatRoleLabel(role)}
              </Tag>
            ))}
          </div>
          {options?.showClose ? (
            <button
              type="button"
              className="sidebar-user-close"
              onClick={onCloseMobileNav}
              aria-label="Cerrar menú"
            >
              <CloseOutlined aria-hidden />
            </button>
          ) : null}
        </div>

        <Menu
          className="sidebar-nav"
          mode="inline"
          selectedKeys={[selectedMenuKey]}
          style={{ borderRight: 0 }}
          onClick={handleMenuClick}
          items={menuItems}
        />
      </div>

      <aside
        className="sidebar-sync-order"
        aria-label="Orden recomendado de sincronización"
      >
        <div className="sidebar-sync-order__top">
          <span className="sidebar-sync-order__badge">
            <ThunderboltOutlined aria-hidden />
            Importante
          </span>
          <p className="sidebar-sync-order__label">
            Orden de sincronización
          </p>
        </div>

        <ol className="sidebar-sync-order__steps">
          <li className="sidebar-sync-order__step">
            <span className="sidebar-sync-order__num">1</span>
            <span className="sidebar-sync-order__name">Catálogo</span>
          </li>
          <li className="sidebar-sync-order__arrow" aria-hidden>
            <RightOutlined />
          </li>
          <li className="sidebar-sync-order__step">
            <span className="sidebar-sync-order__num">2</span>
            <span className="sidebar-sync-order__name">Inventario</span>
          </li>
          <li className="sidebar-sync-order__arrow" aria-hidden>
            <RightOutlined />
          </li>
          <li className="sidebar-sync-order__step">
            <span className="sidebar-sync-order__num">3</span>
            <span className="sidebar-sync-order__name">Precios</span>
          </li>
        </ol>

        <p className="sidebar-sync-order__hint">
          Ejecuta los módulos en este orden para actualizar la tienda
          correctamente.
        </p>
      </aside>
    </section>
  );

  return (
    <>
      <Sider width={260} className="sidebar__container sidebar__container--desktop">
        {renderSidebarPanel()}
      </Sider>

      <Drawer
        className="sidebar-drawer"
        placement="left"
        width={260}
        open={mobileNavOpen}
        onClose={onCloseMobileNav}
        title={null}
        closable={false}
        destroyOnClose={false}
      >
        {renderSidebarPanel({ showClose: true })}
      </Drawer>
    </>
  );
};

export default Sidebar;
